import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, Twitter, ArrowUpRight, Phone, Mail } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../data/content";

const COLUMNS = [
  {
    title: "COMPANY",
    links: [
      { label: "About", to: "/about" },
      { label: "Our Work", to: "/work" },
      { label: "Careers", to: "/contact" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "SERVICES",
    links: [
      { label: "Social Media", to: "/#services" },
      { label: "Branding", to: "/#services" },
      { label: "Influencer Marketing", to: "/#services" },
      { label: "Advertising", to: "/#services" },
      { label: "Content", to: "/#services" },
    ],
  },
  {
    title: "RESOURCES",
    links: [
      { label: "Insights", to: "/insights" },
      { label: "Case Studies", to: "/work" },
      { label: "FAQs", to: "/contact" },
    ],
  },
  {
    title: "LEGAL",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
  },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "#" },
  { icon: Facebook, label: "Facebook", href: "#" },
  { icon: Linkedin, label: "LinkedIn", href: "#" },
  { icon: Youtube, label: "YouTube", href: "#" },
  { icon: Twitter, label: "X", href: "#" },
];

export default function Footer() {
  return (
    <footer className="relative bg-[#05080F] border-t border-slate-800/60 noise-overlay" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="py-16 lg:py-20 flex flex-col lg:flex-row lg:items-center justify-between gap-8 border-b border-slate-800/60">
          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-blue-400 mb-3">Next step</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-50">
              READY TO GET <span className="text-gradient">NOTICED?</span>
            </h2>
          </div>
          <Link
            to="/start"
            data-testid="footer-cta-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-all duration-300 active:scale-95 shadow-xl shadow-blue-600/25 self-start lg:self-auto"
          >
            Start a Project
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="py-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10">
          <div className="col-span-2">
            <Logo />
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
              Where media meets influence. A media-tech and social growth company for brands, businesses, creators and organizations.
            </p>
            <div className="mt-6 space-y-2 text-sm text-slate-400">
              <a href={`tel:+91${COMPANY.phone}`} data-testid="footer-phone-link" className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Phone size={14} className="text-blue-400" /> {COMPANY.phoneDisplay}
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="footer-email-link" className="flex items-center gap-2 hover:text-blue-400 transition-colors break-all">
                <Mail size={14} className="text-blue-400 shrink-0" /> {COMPANY.email}
              </a>
            </div>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-slate-700/70 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/60 transition-all duration-300"
                >
                  <s.icon size={15} />
                </a>
              ))}
            </div>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[\s&]+/g, "-")}`} className="text-sm text-slate-400 hover:text-blue-400 transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="py-6 border-t border-slate-800/60 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500 text-center sm:text-left">
            © 2026 TRENDSIGHT MEDIA PRIVATE LIMITED. All Rights Reserved.
          </p>
          <p className="text-[11px] text-slate-600 font-mono-accent">CIN: {COMPANY.cin} · GSTIN: {COMPANY.gstin}</p>
        </div>
      </div>
    </footer>
  );
}
